import DOMPurify from 'dompurify'

/**
 * The formatting a Quill rich-text editor produces. Anything else — scripts, iframes, forms,
 * inline styles, event handlers — is dropped by DOMPurify along with its unsafe URLs.
 */
const ALLOWED_TAGS = [
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'blockquote',
  'ul',
  'ol',
  'li',
  'a',
  'img',
  'span',
]

/** `data-list` is how Quill 2 marks a bullet item inside its `<ol>`-only list output. */
const ALLOWED_ATTR = ['href', 'src', 'alt', 'class', 'data-list']

/** Quill's nesting classes are the only ones kept; every other class is stripped. */
const QUILL_INDENT_CLASS_PATTERN = /^ql-indent-[1-8]$/

/**
 * Only the tags Quill emits count as markup, so a plain-text placeholder such as `<insured name>`
 * stays verbatim instead of being parsed as an unknown element and stripped.
 */
const HTML_TAG_PATTERN = new RegExp(`<\\/?(?:${ALLOWED_TAGS.join('|')})(?:\\s[^>]*)?\\/?>`, 'i')

/** True when the text carries HTML tags, as opposed to plain text written with newlines. */
export const containsHtmlMarkup = (text: string): boolean => {
  return HTML_TAG_PATTERN.test(text)
}

/**
 * Sanitises partner-authored rich-text HTML (Quill editor output) for `dangerouslySetInnerHTML`:
 * keeps headings, emphasis, lists with their `ql-indent-N` nesting, paragraphs, links and inline
 * images, and opens links in a new tab without handing over `window.opener`.
 */
export const sanitizeRichTextHtml = (html: string): string => {
  const fragment = DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    RETURN_DOM_FRAGMENT: true,
  })

  fragment.querySelectorAll('[class]').forEach((element) => {
    const indentClasses = Array.from(element.classList).filter((className) => {
      return QUILL_INDENT_CLASS_PATTERN.test(className)
    })

    if (indentClasses.length === 0) {
      element.removeAttribute('class')
    } else {
      element.setAttribute('class', indentClasses.join(' '))
    }
  })

  fragment.querySelectorAll('a').forEach((anchor) => {
    anchor.setAttribute('target', '_blank')
    anchor.setAttribute('rel', 'noopener noreferrer')
  })

  const container = document.createElement('div')
  container.appendChild(fragment)

  return container.innerHTML
}

const NEW_TAB_REL = 'noopener noreferrer'

/**
 * An `<a>` start tag, captured as its attributes and its closing `>`. It is matched attribute by
 * attribute, so a `>` inside a quoted value does not end it, and `<abbr>` or `<article>` never match.
 */
const ANCHOR_START_TAG_PATTERN =
  /<a((?:\s+[^\s"'>/=]+(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^\s"'=<>`]+))?)*)(\s*\/?>)/gi

/** One attribute of a start tag: its name, then its double-quoted, single-quoted or bare value. */
const ATTRIBUTE_PATTERN = /\s+([^\s"'>/=]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+)))?/g

const readAttributeValues = (attributes: string): Map<string, string> => {
  const attributeValues = new Map<string, string>()

  Array.from(attributes.matchAll(ATTRIBUTE_PATTERN)).forEach((match) => {
    const [, name, doubleQuotedValue, singleQuotedValue, bareValue] = match

    attributeValues.set(
      name.toLowerCase(),
      doubleQuotedValue ?? singleQuotedValue ?? bareValue ?? '',
    )
  })

  return attributeValues
}

/**
 * Opens every link of admin-authored HTML in a new tab without handing over `window.opener`, so
 * following one never navigates away from the editor preview or the contractor's onboarding.
 *
 * Only `<a>` start tags are rewritten; everything else stays byte for byte. A DOM round trip would
 * re-serialise the whole block and escape a Liquid `{% if attempts < 2 %}` to `&lt;`, which LiquidJS
 * then fails to parse. Links that already open in a new tab are left exactly as authored.
 */
export const openLinksInNewTab = (html: string): string => {
  if (!html) {
    return html
  }

  return html.replace(
    ANCHOR_START_TAG_PATTERN,
    (startTag: string, attributes: string, tagEnd: string) => {
      const attributeValues = readAttributeValues(attributes)

      if (
        attributeValues.get('target') === '_blank' &&
        attributeValues.get('rel') === NEW_TAB_REL
      ) {
        return startTag
      }

      const keptAttributes = attributes.replace(
        ATTRIBUTE_PATTERN,
        (attribute: string, name: string) => {
          const attributeName = name.toLowerCase()

          return attributeName === 'target' || attributeName === 'rel' ? '' : attribute
        },
      )

      return `<a${keptAttributes} target="_blank" rel="${NEW_TAB_REL}"${tagEnd}`
    },
  )
}
